import React from "react";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ROUTES } from "../../routes/routerConfig";
import {
  qrcFlowTypeSelector,
  qrcLanSelector,
  qrcUpgradedToBorrowerSelector,
  clearQrcToken,
  setQrcUpgradedToBorrower,
} from "../../services/redux/slices/authSlice";
import { useAuth } from "../../hooks/useAuth";
import SupportCategoriesGrid from "../../components/common/SupportCategoriesGrid";
import PreLoginHero from "../../components/common/PreLoginHero";
import DigitalChannels from "../../components/common/DigitalChannels";
import { preLoginFontSx } from "../../components/common/preLoginFont";

const TEAL = "#00B3B5";
// Fixed height for the tab card and the rail beside it, so the pair reads as one block whatever
// the page inside is showing. Only applied from md up; on mobile the columns stack and flow.
const SHELL_HEIGHT = 615;

// Shell for the Raise Ticket / View Tickets pages in the pre-login QRC flow ("Yes, I do" /
// "No, I don't"): the same hero + "Before you raise a request" rail + digital channels as the
// Help Center, wrapped around the tab switcher and the page itself.
// A normal authenticated session renders just the Outlet, unchanged from before.
const ContactUsHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const qrcFlowType = useSelector(qrcFlowTypeSelector);
  const qrcLan = useSelector(qrcLanSelector);
  const upgradedToBorrower = useSelector(qrcUpgradedToBorrowerSelector);
  const { isAuthenticated } = useAuth();
  // Pre-login shell only for a genuine pre-login visitor. A logged-in session always wins, even
  // if stale QRC state is still sitting in this tab from an earlier pre-login journey.
  const isQrcFlow = !!qrcFlowType && !isAuthenticated;

  // Pre-login sessions have no login to end — dropping the QRC token is the logout, and
  // QrcRoutes then sends the visitor back to the public Help Center.
  const handleLogout = () => {
    dispatch(clearQrcToken());
    navigate(ROUTES.HelpCenter.locationPath);
  };

  // "No, it didn't help" on a resolution guide is a request to raise a ticket, so take the
  // customer straight to Raise a Request and put the form in view at the top of the page —
  // landing mid-scroll on a page they were already reading hides the thing they were sent for.
  // Scrolling unconditionally covers the case where they are on that tab already, when the
  // navigate() alone is a no-op. Wired to the logged-in rail and to both QRC journeys below;
  // only the public Help Center, where the visitor has no verified account yet and so no form
  // to land on, keeps dismiss-only.
  const handleGuideNotHelpful = () => {
    navigate(ROUTES.ContactUs.raiseRequest.locationPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isRaiseTab = location.pathname === ROUTES.ContactUs.raiseRequest.locationPath;
  // Ticket Details is not one of the two tabs and lays itself out in two columns, so it keeps
  // the hero + channels but skips the tab card and the rail — nesting it inside the 8-column
  // card squeezed its own details/timeline grid.
  const isTicketDetails = location.pathname.startsWith(ROUTES.ContactUs.ticketDetails.locationPath);

  if (!isQrcFlow) {
    // Logged-in session: the app's own chrome (sidebar, breadcrumbs, welcome header) stays as it
    // is — we only add the "Before you raise a request" rail beside the page. Ticket Details is
    // excluded because it already lays itself out in two columns.
    if (isTicketDetails) {
      return <Outlet />;
    }
    return (
      // stretch, so the rail's column is as tall as the ticket list beside it. A sticky element
      // can only travel inside its own column: with flex-start the column shrank to the rail's
      // own height, leaving no room to stick, and it scrolled away with the page (measured).
      // The rail Box inside stays content-height, so this adds no trailing empty teal.
      // No railFillHeight: that variant is absolutely positioned for the pre-login shell.
      // 8/4 matches the pre-login shell; the list's toolbar wraps rather than compressing, so the
      // narrower page column no longer costs the status pills their space.
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <Outlet />
        </Grid>
        <Grid item xs={12} md={4}>
          {/* Pinned while the ticket list scrolls past it. md+ only — on mobile the rail stacks
              under the page, where pinning it would cover the content. maxHeight + overflow so a
              rail taller than the viewport can still be scrolled through; a stuck element taller
              than the screen otherwise hides its own bottom.
              Requires overflowX:"clip" on the layout wrapper (see DashboardLayoutJourny) —
              "hidden" there makes it a scroll container and sticky silently does nothing. */}
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: { md: 16 },
              maxHeight: { md: "calc(100vh - 32px)" },
              overflowY: { md: "auto" },
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#D8DDE2", borderRadius: 3 },
              scrollbarWidth: "thin",
            }}
          >
            <SupportCategoriesGrid layout="rail" onNotHelpful={handleGuideNotHelpful} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (isTicketDetails) {
    return (
      <Box sx={preLoginFontSx}>
        <PreLoginHero variant="request" onLogout={handleLogout} />
        {/* Rises onto the hero like the card on every other pre-login screen. Without it the
            band's bottom third sat empty, since this page has no overlapping card of its own. */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 1.5, sm: 2.5 },
            mt: { xs: "-24px", sm: "-86px" },
            pb: 6,
          }}
        >
          <Outlet />
          <DigitalChannels />
        </Box>
      </Box>
    );
  }

  // xs is an underline tab strip: equal halves, flat, a teal bar under the active label (the
  // -1px pulls it over the strip's hair rule). sm+ keeps the raised pill on a grey track.
  const tabSx = (active: boolean) => ({
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "13px",
    flex: { xs: 1, sm: "0 0 auto" },
    minWidth: 0,
    whiteSpace: "nowrap" as const,
    borderRadius: { xs: 0, sm: "8px" },
    px: { xs: 0.5, sm: 2.5 },
    py: { xs: 1.25, sm: 0.9 },
    color: active ? TEAL : "#6b7280",
    bgcolor: { xs: "transparent", sm: active ? "#fff" : "transparent" },
    boxShadow: { xs: "none", sm: active ? "0px 1px 4px 0px rgba(0,0,0,0.08)" : "none" },
    borderBottom: { xs: `3px solid ${active ? TEAL : "transparent"}`, sm: "none" },
    mb: { xs: "-1px", sm: 0 },
    transition: "color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      bgcolor: {
        xs: active ? "transparent" : "rgba(0,179,181,0.06)",
        sm: active ? "#fff" : "rgba(0,0,0,0.03)",
      },
    },
  });

  return (
    <Box sx={preLoginFontSx}>
      <PreLoginHero variant="request" onLogout={handleLogout} />

      <Box sx={{ pb: 6 }}>
        {/* Full-width with the design's narrow gutters — the layout is not capped at 1200 */}
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 3, sm: 4 } }}>
          {/* The negative margin lives here, not on the card, so the card and the rail rise
              onto the hero together instead of the rail sitting 92px lower. position/zIndex are
              required: the hero is position:relative, so it paints over static siblings — without
              this the rail's top disappears behind the hero band. */}
          <Box sx={{ position: "relative", zIndex: 1, mt: { xs: "-34px", sm: "-92px" } }}>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={8}>
              {/* Fixed height on desktop so the rail beside it has a stable height to match. The
                  card itself never scrolls — the tabs stay put and only the page content below
                  them scrolls (see the wrapper around <Outlet />). */}
              <Box
                sx={{
                  position: "relative",
                  height: { md: SHELL_HEIGHT },
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  border: { xs: "1px solid #EFF1F3", sm: `1.5px solid ${TEAL}` },
                  boxShadow: { xs: "0px 4px 18px 0px rgba(75, 70, 92, 0.08)", sm: "none" },
                  // Matches the verify card: tight side padding, extra top room for the pill.
                  px: { xs: 2.5, sm: 2 },
                  pt: { xs: 2.5, sm: 3.5 },
                  pb: 2.5,
                }}
              >
                {/* Shown once when a "No, I don't" verification matched an existing customer and
                    was upgraded to a borrower session — otherwise landing on the loan-account
                    tools after answering "no" is unexplained. Dismissible; also cleared on logout. */}
                {upgradedToBorrower && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      px: 2,
                      py: 1.25,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: "#FCF6E3",
                    }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#EAB308", fontSize: 20, flexShrink: 0, mt: "1px" }} />
                    <Typography sx={{ fontSize: "13px", color: "#3A3A3A", lineHeight: 1.5, flex: 1 }}>
                      You&apos;re already registered with us as an existing customer
                      {qrcLan ? ` (${qrcLan})` : ""}. We&apos;ve matched your details to your loan
                      account, so you can raise and track requests against it here.
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Dismiss"
                      onClick={() => dispatch(setQrcUpgradedToBorrower(false))}
                      sx={{ p: 0.25, mt: "-2px" }}
                    >
                      <CloseIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                    </IconButton>
                  </Box>
                )}

                {/* Tabs left, LAN read-out right — the design's header row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: "flex", sm: "inline-flex" },
                      width: { xs: "100%", sm: "auto" },
                      bgcolor: { xs: "transparent", sm: "#eef1f5" },
                      borderBottom: { xs: "1px solid #E5E7EB", sm: "none" },
                      borderRadius: { xs: 0, sm: "10px" },
                      p: { xs: 0, sm: 0.5 },
                    }}
                  >
                    <Button
                      onClick={() => navigate(ROUTES.ContactUs.raiseRequest.locationPath)}
                      sx={tabSx(isRaiseTab)}
                    >
                      Raise New Ticket
                    </Button>
                    <Button
                      onClick={() => navigate(ROUTES.ContactUs.viewTickets.locationPath)}
                      sx={tabSx(!isRaiseTab)}
                    >
                      View Existing Tickets
                    </Button>
                  </Box>

                </Box>

                {/* The only scroll region in the card — vertical only, so a long ticket list or
                    form scrolls while the tabs above stay fixed. minHeight:0 is
                    required for a flex child to shrink below its content and actually scroll. */}
                <Box
                  sx={{
                    flex: { md: 1 },
                    minHeight: 0,
                    overflowY: { md: "auto" },
                    overflowX: "hidden",
                    pr: { md: 0.5 },
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
                    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "#D8DDE2", borderRadius: 3 },
                    scrollbarWidth: "thin",
                    scrollbarColor: "#D8DDE2 transparent",
                  }}
                >
                  <Outlet />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Positioning context for the rail, which is absolute on desktop so the card
                  decides the row height and the rail matches it exactly. */}
              <Box sx={{ position: "relative", height: { md: "100%" } }}>
                {/* QRC pre-login shell — both journeys, "Yes, I do" (borrower) and "No, I don't"
                    (non-borrower). Each has its own Raise a Request form to land on. */}
                <SupportCategoriesGrid layout="rail" railFillHeight onNotHelpful={handleGuideNotHelpful} />
              </Box>
            </Grid>
          </Grid>
          </Box>

          <DigitalChannels />
        </Box>
      </Box>
    </Box>
  );
};

export default ContactUsHub;
